import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  FileVideo,
  RefreshCw,
  Upload,
  UserPlus,
  X,
} from 'lucide-react';
import {
  api,
  type Player,
  type PowerImportConfirmRow,
  type PowerImportJob,
  type PowerImportMatchedRow,
  type PowerImportPreview,
  type PowerImportUnmatchedRow,
  type Rank,
} from '@/lib/api';

// Total-power video import (Plan 044). Same flow as ImportKillsDialog,
// plus the option to create unmatched names as new players.

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  initialJobId?: string | null;
}

const RANKS: Rank[] = ['R5', 'R4', 'R3', 'R2', 'R1'];
const SUGGESTION_PREPICK_SCORE = 0.7;

// Unmatched target: '' = skip, 'new' = create player, otherwise a player id.
type Target = '' | 'new' | number;

type Resolution =
  | { kind: 'matched'; ocrName: string; row: PowerImportMatchedRow; included: boolean }
  | {
      kind: 'unmatched';
      ocrName: string;
      row: PowerImportUnmatchedRow;
      target: Target;
      newName: string;
      newRank: Rank;
      included: boolean;
    };

function formatMillions(m: number): string {
  return m.toFixed(1);
}

function formatRaw(n: number): string {
  return n.toLocaleString('en-US');
}

function formatDelta(d: number): string {
  return `${d > 0 ? '+' : ''}${d.toFixed(1)}`;
}

function toConfirmRow(r: Resolution): PowerImportConfirmRow | null {
  if (!r.included) return null;
  if (r.kind === 'matched') return { playerId: r.row.player.id, power: r.row.power };
  if (r.target === 'new') {
    const name = r.newName.trim();
    return name ? { create: { name, rank: r.newRank }, power: r.row.power } : null;
  }
  if (typeof r.target === 'number') return { playerId: r.target, power: r.row.power };
  return null;
}

export default function ImportPowerDialog({
  open,
  onClose,
  onImported,
  initialJobId = null,
}: Props) {
  const qc = useQueryClient();
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(initialJobId);
  const [preview, setPreview] = useState<PowerImportPreview | null>(null);
  const [resolutions, setResolutions] = useState<Resolution[]>([]);

  const playersQuery = useQuery({
    queryKey: ['players', 'all-for-import'],
    queryFn: () => api.listPlayers({ includeInactive: true }),
    enabled: open && !!preview,
  });

  const jobQuery = useQuery({
    queryKey: ['power-import-job', jobId],
    queryFn: () => api.getPowerImportJob(jobId!),
    enabled: !!jobId && !preview,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s && (s === 'done' || s === 'failed') ? false : 1500;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!videoFile) throw new Error('No video');
      return api.uploadPowerImportVideo(videoFile);
    },
    onSuccess: (data) => setJobId(data.jobId),
  });

  const previewMutation = useMutation({
    mutationFn: (rows: Array<{ name: string; power: number }>) => api.previewPowerImport(rows),
    onSuccess: (data) => {
      setPreview(data);
      setResolutions([
        ...data.matched.map<Resolution>((row) => ({
          kind: 'matched',
          ocrName: row.ocrName,
          row,
          included: !row.unchanged,
        })),
        ...data.unmatched.map<Resolution>((row) => ({
          kind: 'unmatched',
          ocrName: row.ocrName,
          row,
          // Only pre-pick a suggestion that's a plausible typo; weak
          // matches are more likely a new member.
          target:
            row.suggestions[0] && row.suggestions[0].score >= SUGGESTION_PREPICK_SCORE
              ? row.suggestions[0].playerId
              : 'new',
          newName: row.ocrName,
          newRank: 'R1',
          included: false,
        })),
      ]);
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => api.retryPowerImportJob(jobId!),
    onSuccess: (data: PowerImportJob) => {
      qc.setQueryData(['power-import-job', jobId], data);
    },
  });

  const confirmRows = useMemo(
    () => resolutions.map(toConfirmRow).filter((r): r is PowerImportConfirmRow => r != null),
    [resolutions],
  );

  const confirmMutation = useMutation({
    mutationFn: () => api.confirmPowerImport(confirmRows, jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roster'] });
      qc.invalidateQueries({ queryKey: ['players'] });
      onImported();
      reset();
      onClose();
    },
  });

  useEffect(() => {
    const j = jobQuery.data;
    if (!j || preview) return;
    if (j.status === 'done' && j.extraction) {
      previewMutation.mutate(j.extraction.rows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobQuery.data?.status]);

  function reset() {
    setVideoFile(null);
    setJobId(null);
    setPreview(null);
    setResolutions([]);
    uploadMutation.reset();
    previewMutation.reset();
    confirmMutation.reset();
    retryMutation.reset();
  }

  if (!open) return null;

  const stageText = (() => {
    const j = jobQuery.data;
    if (!j) return 'Uploading…';
    if (j.status === 'failed') return `Failed: ${j.error ?? 'unknown'}`;
    if (j.stage === 'uploading') return 'Uploading video…';
    if (j.stage === 'processing') return 'Extracting power values…';
    return 'Working…';
  })();

  const isFailed = jobQuery.data?.status === 'failed';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-4xl rounded-lg border border-slate-800 bg-slate-900 p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Import power from video</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Step 1: pick file */}
        {!jobId && !preview && (
          <div className="mt-4 space-y-4">
            <div className="rounded border border-slate-800 bg-slate-950/40 px-3 py-2 text-xs text-slate-400">
              Record the in-game alliance member list showing each member's power.
              Pan slowly so every row is visible for at least a second.
            </div>

            <label
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
              }}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f && f.type.startsWith('video/')) setVideoFile(f);
              }}
              className="flex cursor-pointer flex-col gap-2 rounded border border-dashed border-slate-700 bg-slate-950/50 p-4 text-sm text-slate-300 hover:border-slate-500"
            >
              <span className="inline-flex items-center gap-2 text-slate-200">
                <FileVideo size={16} />
                {videoFile ? videoFile.name : 'Drop or select .mp4 / .mov'}
              </span>
              <input
                type="file"
                accept="video/mp4,video/quicktime"
                className="hidden"
                onChange={(e) => setVideoFile(e.target.files?.[0] ?? null)}
              />
            </label>

            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={() => uploadMutation.mutate()}
                disabled={!videoFile || uploadMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
              >
                <Upload size={14} /> Process
              </button>
            </div>
            {uploadMutation.isError && (
              <div className="text-sm text-red-400">
                {(uploadMutation.error as Error)?.message ?? 'Upload failed'}
              </div>
            )}
          </div>
        )}

        {/* Step 2: job progress */}
        {jobId && !preview && (
          <div className="mt-6 space-y-3">
            {!isFailed && (
              <div className="flex items-center gap-3 text-sm text-slate-300">
                <div className="h-2 flex-1 overflow-hidden rounded bg-slate-800">
                  <div
                    className="h-full bg-indigo-500 transition-all"
                    style={{
                      width:
                        jobQuery.data?.stage === 'processing'
                          ? '80%'
                          : jobQuery.data?.stage === 'uploading'
                            ? '40%'
                            : '10%',
                    }}
                  />
                </div>
                <span>{stageText}</span>
              </div>
            )}

            {isFailed && (
              <div className="space-y-2 rounded border border-red-800 bg-red-950/40 p-3 text-sm text-red-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="mt-0.5" />
                  <div>
                    <div className="font-medium">Gemini extraction failed</div>
                    <div className="mt-1 text-xs text-red-300/80">
                      {jobQuery.data?.error ?? 'unknown error'}
                    </div>
                    {typeof jobQuery.data?.attempts === 'number' && (
                      <div className="text-xs text-red-300/60">
                        Attempts: {jobQuery.data.attempts}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => retryMutation.mutate()}
                    disabled={retryMutation.isPending || !jobQuery.data?.videoAvailable}
                    className="inline-flex items-center gap-1.5 rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
                  >
                    <RefreshCw size={14} /> Retry
                  </button>
                  <button
                    onClick={() => reset()}
                    className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
                  >
                    Upload a different video
                  </button>
                </div>
                {!jobQuery.data?.videoAvailable && (
                  <div className="text-xs text-red-300/70">
                    Stored video is no longer on disk — re-upload to retry.
                  </div>
                )}
                {retryMutation.isError && (
                  <div className="text-xs text-red-300">
                    {(retryMutation.error as Error)?.message ?? 'Retry failed'}
                  </div>
                )}
              </div>
            )}

            {!isFailed && (
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Runs in the background — you can close this dialog and check back later.</span>
                <button
                  onClick={onClose}
                  className="rounded border border-slate-700 px-3 py-1 text-sm text-slate-300 hover:bg-slate-800"
                >
                  Check later
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: preview */}
        {preview && (
          <>
            <PreviewView
              preview={preview}
              resolutions={resolutions}
              setResolutions={setResolutions}
              players={playersQuery.data ?? []}
              includedCount={confirmRows.length}
              onCancel={() => reset()}
              onConfirm={() => confirmMutation.mutate()}
              confirming={confirmMutation.isPending}
            />
            {confirmMutation.isError && (
              <div className="mt-2 rounded border border-red-800 bg-red-950/40 p-2 text-sm text-red-200">
                {(confirmMutation.error as Error)?.message ?? 'Import failed'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PreviewView({
  preview,
  resolutions,
  setResolutions,
  players,
  includedCount,
  onCancel,
  onConfirm,
  confirming,
}: {
  preview: PowerImportPreview;
  resolutions: Resolution[];
  setResolutions: (r: Resolution[]) => void;
  players: Player[];
  includedCount: number;
  onCancel: () => void;
  onConfirm: () => void;
  confirming: boolean;
}) {
  function update(ocrName: string, patch: Partial<Extract<Resolution, { kind: 'unmatched' }>>) {
    setResolutions(
      resolutions.map((r) => (r.kind === 'unmatched' && r.ocrName === ocrName ? { ...r, ...patch } : r)),
    );
  }
  function toggle(ocrName: string) {
    setResolutions(
      resolutions.map((r) => (r.ocrName === ocrName ? { ...r, included: !r.included } : r)),
    );
  }

  // Players already claimed by an included row — can't be picked twice.
  const usedIds = useMemo(() => {
    const ids = new Map<number, string>();
    for (const r of resolutions) {
      if (!r.included) continue;
      if (r.kind === 'matched') ids.set(r.row.player.id, r.ocrName);
      else if (typeof r.target === 'number') ids.set(r.target, r.ocrName);
    }
    return ids;
  }, [resolutions]);

  const sortedPlayers = useMemo(
    () =>
      players
        .filter((p) => !p.deleted)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [players],
  );

  const decreases = resolutions.filter((r) => r.kind === 'matched' && r.row.decrease).length;
  const unchanged = resolutions.filter((r) => r.kind === 'matched' && r.row.unchanged).length;
  const creates = resolutions.filter(
    (r) => r.kind === 'unmatched' && r.included && r.target === 'new',
  ).length;

  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="text-slate-300">
          <b className="text-white">{preview.matched.length}</b> matched ·{' '}
          {preview.unmatched.length} unmatched
          {decreases > 0 && (
            <> · <span className="text-amber-400">{decreases} decreased</span></>
          )}
          {unchanged > 0 && (
            <> · <span className="text-slate-500">{unchanged} unchanged</span></>
          )}
        </span>
        <span className="text-slate-500">
          {includedCount} rows will be imported
          {creates > 0 && <> · {creates} new players</>}
        </span>
      </div>

      <div className="max-h-[28rem] overflow-y-auto rounded border border-slate-800">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-950/80 text-xs text-slate-500">
            <tr>
              <th className="w-8 px-2 py-2 text-left"></th>
              <th className="px-2 py-2 text-left">From video</th>
              <th className="px-2 py-2 text-left">Player</th>
              <th className="px-2 py-2 text-right">Power (raw)</th>
              <th className="px-2 py-2 text-right">M</th>
              <th className="px-2 py-2 text-right">Baseline (M)</th>
              <th className="px-2 py-2 text-right">Δ</th>
            </tr>
          </thead>
          <tbody>
            {resolutions.map((r) => {
              const canInclude =
                r.kind === 'matched' ||
                (r.target === 'new'
                  ? r.newName.trim() !== ''
                  : r.target !== '' &&
                    (!usedIds.has(r.target) || usedIds.get(r.target) === r.ocrName));
              const delta =
                r.kind === 'matched' && r.row.baselinePowerM != null
                  ? r.row.powerM - r.row.baselinePowerM
                  : null;
              return (
                <tr
                  key={r.ocrName}
                  className={
                    'border-t border-slate-800/70 align-top ' +
                    (r.kind === 'matched' && r.row.decrease ? 'bg-amber-950/20' : '')
                  }
                >
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={r.included}
                      onChange={() => toggle(r.ocrName)}
                      disabled={!canInclude}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-slate-300">
                    {r.ocrName}
                    {r.kind === 'matched' && r.row.decrease && (
                      <span className="ml-2 rounded bg-amber-950/60 px-1.5 py-0.5 text-[10px] uppercase text-amber-300">
                        decrease
                      </span>
                    )}
                    {r.kind === 'matched' && r.row.unchanged && (
                      <span className="ml-2 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase text-slate-400">
                        unchanged
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {r.kind === 'matched' ? (
                      <span className="inline-flex items-center gap-1.5 text-slate-200">
                        <CheckCircle2 size={12} className="text-emerald-500" />
                        {r.row.player.displayName ?? r.row.player.name}
                        {!r.row.player.active && (
                          <span className="rounded bg-amber-900/30 px-1 text-[10px] uppercase text-amber-300">
                            inactive
                          </span>
                        )}
                      </span>
                    ) : (
                      <div className="space-y-1.5">
                        <select
                          value={r.target === '' ? '' : String(r.target)}
                          onChange={(e) => {
                            const v = e.target.value;
                            const target: Target = v === '' ? '' : v === 'new' ? 'new' : Number(v);
                            update(r.ocrName, { target, included: target !== '' });
                          }}
                          className="w-full rounded border border-slate-700 bg-slate-950 px-1.5 py-1 text-xs text-slate-100"
                        >
                          <option value="">— skip —</option>
                          <option value="new">+ Create new player</option>
                          {r.row.suggestions.length > 0 && (
                            <optgroup label="Suggestions">
                              {r.row.suggestions.map((s) => (
                                <option
                                  key={s.playerId}
                                  value={s.playerId}
                                  disabled={usedIds.has(s.playerId) && usedIds.get(s.playerId) !== r.ocrName}
                                >
                                  {s.displayName ?? s.name} ({Math.round(s.score * 100)}%)
                                </option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label="All players">
                            {sortedPlayers.map((p) => (
                              <option
                                key={p.id}
                                value={p.id}
                                disabled={usedIds.has(p.id) && usedIds.get(p.id) !== r.ocrName}
                              >
                                {p.name}
                                {p.displayName ? ` (${p.displayName})` : ''}
                                {p.active ? '' : ' — inactive'}
                              </option>
                            ))}
                          </optgroup>
                        </select>
                        {r.target === 'new' && (
                          <div className="flex items-center gap-1.5">
                            <UserPlus size={12} className="shrink-0 text-indigo-400" />
                            <input
                              value={r.newName}
                              onChange={(e) =>
                                update(r.ocrName, {
                                  newName: e.target.value,
                                  included: r.included && e.target.value.trim() !== '',
                                })
                              }
                              placeholder="Player name"
                              maxLength={64}
                              className="min-w-0 flex-1 rounded border border-slate-700 bg-slate-950 px-1.5 py-1 text-xs text-slate-100"
                            />
                            <select
                              value={r.newRank}
                              onChange={(e) => update(r.ocrName, { newRank: e.target.value as Rank })}
                              className="rounded border border-slate-700 bg-slate-950 px-1.5 py-1 text-xs text-slate-100"
                            >
                              {RANKS.map((rank) => (
                                <option key={rank} value={rank}>
                                  {rank}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">
                    {formatRaw(r.row.power)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">
                    {formatMillions(r.row.powerM)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">
                    {r.kind === 'matched' && r.row.baselinePowerM != null
                      ? formatMillions(r.row.baselinePowerM)
                      : '—'}
                  </td>
                  <td
                    className={
                      'px-2 py-1.5 text-right tabular-nums ' +
                      (delta == null || delta === 0
                        ? 'text-slate-500'
                        : delta > 0
                          ? 'text-emerald-400'
                          : 'text-amber-400')
                    }
                  >
                    {delta == null ? '—' : formatDelta(delta)}
                  </td>
                </tr>
              );
            })}
            {resolutions.length === 0 && (
              <tr>
                <td colSpan={7} className="px-2 py-6 text-center text-slate-500">
                  No rows extracted.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={confirming || includedCount === 0}
          className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-40"
        >
          Import {includedCount} rows
        </button>
      </div>
    </div>
  );
}
